import { useState } from "react";
import { Icon } from "@iconify/react";
import { Tabs } from "antd";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import type { FlaggedRecord, ReviewedItem, ReviewDecision } from "@/shared/types/humanReview";

interface HumanReviewQueueSectionProps {
  records: FlaggedRecord[];
  recentlyReviewed: ReviewedItem[];
}

type TabKey = "flagged" | "reviewed" | "skipped";
type RecordState = "pending" | "reviewed" | "skipped";

function formatDaysAgo(days: number): string {
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function formatDate(dateString: string): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return dateString;
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export function HumanReviewQueueSection({ records, recentlyReviewed }: HumanReviewQueueSectionProps) {
  const [activeTab, setActiveTab] = useState<TabKey>("flagged");
  const [recordStates, setRecordStates] = useState<Record<string, RecordState>>({});
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({});
  const [selectedId, setSelectedId] = useState<string | null>(records[0]?.id ?? null);
  const [feed, setFeed] = useState<ReviewedItem[]>(recentlyReviewed);

  const getState = (id: string): RecordState => recordStates[id] ?? "pending";

  const flagged = records.filter((r) => getState(r.id) === "pending");
  const reviewed = records.filter((r) => getState(r.id) === "reviewed");
  const skipped = records.filter((r) => getState(r.id) === "skipped");

  const listForTab = activeTab === "flagged" ? flagged : activeTab === "reviewed" ? reviewed : skipped;
  const selected = records.find((r) => r.id === selectedId) ?? null;

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    const list = tab === "flagged" ? flagged : tab === "reviewed" ? reviewed : skipped;
    setSelectedId(list[0]?.id ?? null);
  };

  const handleDecision = (record: FlaggedRecord, decision: ReviewDecision) => {
    setRecordStates((current) => ({ ...current, [record.id]: "reviewed" }));
    setDecisions((current) => ({ ...current, [record.id]: decision }));
    setFeed((current) => [
      {
        id: `live-${record.id}-${current.length}`,
        category: "Medication",
        name: record.name,
        description: decision === "accepted" ? "You confirmed this information." : "You rejected this information.",
        decision,
        reviewedDaysAgo: 0,
      },
      ...current,
    ]);
    const remaining = flagged.filter((r) => r.id !== record.id);
    setSelectedId(remaining[0]?.id ?? null);
  };

  const handleSkip = (record: FlaggedRecord) => {
    setRecordStates((current) => ({ ...current, [record.id]: "skipped" }));
    const remaining = flagged.filter((r) => r.id !== record.id);
    setSelectedId(remaining[0]?.id ?? null);
  };

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: "flagged", label: "Flagged Records", count: flagged.length },
    { key: "reviewed", label: "Reviewed", count: reviewed.length },
    { key: "skipped", label: "Skipped", count: skipped.length },
  ];

  return (
    <div className="p-6 lg:p-8">
      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => handleTabChange(key as TabKey)}
        items={tabs.map((tab) => ({
          key: tab.key,
          label: (
            <span className="flex items-center gap-3">
              {tab.label}
              <span
                className={cn(
                  "flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold",
                  activeTab === tab.key ? "bg-[#111827] text-white" : "bg-[#F3F4F6] text-[#6B7280]",
                )}
              >
                {tab.count}
              </span>
            </span>
          ),
        }))}
        className={[
          "[&_.ant-tabs-tab]:!px-3 [&_.ant-tabs-tab]:!py-3 [&_.ant-tabs-tab+.ant-tabs-tab]:!ml-10",
          "[&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[#111827] [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!font-bold",
          "[&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:![text-shadow:none]",
          "[&_.ant-tabs-tab:not(.ant-tabs-tab-active)_.ant-tabs-tab-btn]:!text-[#9CA3AF] [&_.ant-tabs-tab:not(.ant-tabs-tab-active)_.ant-tabs-tab-btn]:!font-bold",
          "[&_.ant-tabs-tab:not(.ant-tabs-tab-active)_.ant-tabs-tab-btn:hover]:!text-[#111827]",
          "[&_.ant-tabs-ink-bar]:!bg-[#111827] [&_.ant-tabs-ink-bar]:!h-[2px]",
          "[&_.ant-tabs-nav]:!mb-5 [&_.ant-tabs-nav-wrap]:!border-b [&_.ant-tabs-nav-wrap]:!border-[#E5E7EB]",
          "[&_.ant-tabs-nav-list]:!border-b-0",
        ].join(" ")}
      />

      <div className="grid items-start gap-5 lg:grid-cols-[1fr_1.3fr]">
        {/* Left — record list */}
        <div className="grid gap-5">
          {listForTab.length === 0 ? (
            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center text-sm text-[#6B7280]">
              {activeTab === "flagged" ? "You're all caught up! Nothing left to review." : `No ${activeTab} records yet.`}
            </div>
          ) : (
            listForTab.map((record) => {
              const isSelected = record.id === selectedId;
              return (
                <Button
                  key={record.id}
                  variant="outline"
                  onClick={() => setSelectedId(record.id)}
                  className={cn(
                    "!block !h-auto !w-full !whitespace-normal !rounded-xl !px-4 !py-3 !text-left !shadow-sm",
                    isSelected
                      ? "!border-[#E5E7EB] !bg-[#FFF8F8] hover:!border-[#E5E7EB] hover:!bg-[#FFF8F8]"
                      : "!border-[#E5E7EB] !bg-white hover:!bg-[#FFF8F8] hover:!shadow-md",
                  )}
                >
                  <div className="flex min-w-0 items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="min-w-0 break-words text-sm font-bold text-[#0A2947]">{record.name}</h3>
                      <span className="shrink-0 rounded-md bg-[#E4002B]/70 px-2 py-0.5 text-[10px] font-bold text-white">{record.dosage}</span>
                    </div>
                    {record.statusTag === "active" ? (
                      <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-[#16A34A]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
                        Active
                      </span>
                    ) : (
                      <span className="shrink-0 text-xs font-medium text-[#9CA3AF]">Past</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[#6B7280]">
                    Prescribed By <span className="font-semibold text-[#111827]">{record.prescribedBy}</span>
                  </p>
                </Button>
              );
            })
          )}
        </div>

        {/* Right — detail + recently reviewed. Sticky so it stays in view while
            scrolling a long left-hand list, instead of scrolling away above the
            fold (matches the pattern in FhirExportView's "Export Summary"). */}
        <div className="sticky top-5">
          {selected ? (
            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
                    <Icon icon="lucide:pill" className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-extrabold text-[#0A2947]">
                      {selected.name} {selected.dosage}
                    </h2>
                    {selected.statusTag === "active" && (
                      <span className="rounded-md bg-[#E4002B]/70 px-2 py-0.5 text-[10px] font-bold text-white">Active</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="link"
                  onClick={() => setSelectedId(null)}
                  aria-label="Close"
                  className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#111827]"
                >
                  <Icon icon="lucide:x" className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-1 pl-14 text-xs text-[#9CA3AF]">Flagged {formatDaysAgo(selected.flaggedDaysAgo)}</p>

              <div className="mt-5 rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-4">
                <p className="text-xs font-extrabold tracking-wide text-[#E4002B]">REASON:</p>
                <p className="mt-1 text-sm text-[#4B5563]">{selected.reason}</p>
              </div>

              <div className="mt-5 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                <h3 className="mb-4 text-sm font-extrabold text-[#0A2947]">Extracted Information</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
                  {[
                    { label: "Medication", value: selected.extracted.medication },
                    { label: "Dosage", value: selected.extracted.dosage },
                    { label: "Frequency", value: selected.extracted.frequency },
                    { label: "Route", value: selected.extracted.route },
                    { label: "Prescribed By", value: selected.extracted.prescribedBy },
                    { label: "Start Date", value: formatDate(selected.extracted.startDate) },
                  ].map((field) => (
                    <div key={field.label}>
                      <p className="text-xs text-[#9CA3AF]">{field.label}</p>
                      <p className="mt-0.5 text-sm font-bold text-[#111827]">{field.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {getState(selected.id) === "pending" ? (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#E5E7EB] pt-5">
                  <div>
                    <p className="text-sm font-semibold text-[#111827]">Please confirm</p>
                    <p className="text-xs text-[#6B7280]">This information is correct.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleDecision(selected, "accepted")}
                      className="!flex !h-auto !items-center !gap-1.5 !rounded-lg !bg-[#0A2947] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90"
                    >
                      <Icon icon="lucide:check" className="h-4 w-4 !text-white" />
                      Accept
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDecision(selected, "rejected")}
                      className="!flex !h-auto !items-center !gap-1.5 !rounded-lg !border-[#E4002B]/40 !px-4 !py-2 !text-sm !font-semibold !text-[#E4002B] hover:!border-[#E4002B]/40 hover:!bg-[#FFF2F4] hover:!text-[#E4002B]"
                    >
                      <Icon icon="lucide:x" className="h-4 w-4 !text-[#E4002B]" />
                      Reject
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSkip(selected)}
                      className="!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-semibold !text-[#6B7280] hover:!bg-[#F9FAFB] hover:!text-[#6B7280]"
                    >
                      Skip
                    </Button>
                  </div>
                </div>
              ) : getState(selected.id) === "reviewed" ? (
                <div className="mt-5 flex items-center justify-between border-t border-[#E5E7EB] pt-5">
                  <p className="text-sm text-[#6B7280]">You already reviewed this record.</p>
                  <span
                    className={cn(
                      "rounded-full px-3 py-1 text-xs font-semibold",
                      decisions[selected.id] === "accepted" ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-[#FEE2E2] text-[#E4002B]",
                    )}
                  >
                    {decisions[selected.id] === "accepted" ? "Accepted" : "Rejected"}
                  </span>
                </div>
              ) : (
                <div className="mt-5 flex items-center justify-between border-t border-[#E5E7EB] pt-5">
                  <p className="text-sm text-[#6B7280]">You skipped this record.</p>
                  <Button
                    onClick={() => setRecordStates((current) => ({ ...current, [selected.id]: "pending" }))}
                    className="!h-auto !rounded-lg !bg-[#0A2947] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90"
                  >
                    Review Now
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center text-sm text-[#6B7280]">
              {records.length === 0 ? "Nothing to review yet." : "Select a record from the list to review it."}
            </div>
          )}

          {feed.length > 0 && (
            <div className="mt-5">
              <h3 className="mb-3 text-sm font-extrabold text-[#0A2947]">Recently Reviewed</h3>
              <div className="grid gap-4">
                {feed.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 shadow-sm">
                    <div
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                        item.decision === "accepted" ? "bg-[#DCFCE7]" : "bg-[#FEE2E2]",
                      )}
                    >
                      {item.decision === "accepted" ? (
                        <Icon icon="lucide:check" className="h-5 w-5 text-[#16A34A]" />
                      ) : (
                        <Icon icon="lucide:x" className="h-5 w-5 text-[#E4002B]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-[#9CA3AF]">{item.category}</p>
                      <p className="truncate text-sm font-bold text-[#0A2947]">{item.name}</p>
                      <p className="truncate text-xs text-[#6B7280]">{item.description}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span
                        className={cn(
                          "inline-block rounded-full px-3 py-1 text-xs font-semibold",
                          item.decision === "accepted" ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-[#FEE2E2] text-[#E4002B]",
                        )}
                      >
                        {item.decision === "accepted" ? "Accepted" : "Rejected"}
                      </span>
                      <p className="mt-1 text-[11px] text-[#9CA3AF]">{formatDaysAgo(item.reviewedDaysAgo)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
